import type { PlaceDto, PlaceSummary } from '@/api/types/Place';

// CHR-06 §6 / §8 — the pure rules that must each have exactly ONE home, because
// several call sites depend on them being identical: the Adresář row copy
// button, the mobile card copy button and the jízda-editor autocomplete insert.

/**
 * The single-line INSERT format: `Název — Adresa` (em dash). Used by the jízda
 * editor's place autocomplete (contract §E) — deliberately NOT by the copy
 * buttons, which copy the whole row (`formatPlaceSms`).
 * A missing or blank address collapses to the bare name — the client's table
 * genuinely has a row without an address („volat, navede").
 */
export const formatPlaceCopy = (
  place: Pick<PlaceSummary, 'name' | 'address'>,
): string => {
  const address = place.address?.trim() ?? '';
  return address.length > 0 ? `${place.name} — ${address}` : place.name;
};

// The „Kontakt:" prefix is part of the SMS payload a dispatcher pastes into a
// message to a driver — not UI chrome — so it stays a constant here rather than
// an i18n key: this helper is pure (no `t`) precisely because three call sites
// must produce a byte-identical string, and the message body is Czech-only by
// contract (`zadani.md §6`).
const SMS_CONTACT_PREFIX = 'Kontakt: ';

/**
 * The WHOLE row, formatted for pasting into an SMS to a driver (user request
 * 2026-09-17: „kopírování bude celý řádek – všechny informace – odentrované
 * nějak chytře – půjde to do SMS"):
 *
 * ```
 * <název>
 * <adresa>
 * Kontakt: <kontakt>
 * <poznámka>
 * ```
 *
 * Blank fields are skipped entirely (no empty lines, no trailing newline), each
 * value is trimmed, and the type is deliberately omitted — irrelevant to the
 * driver. A place with nothing but a name copies as just the name.
 *
 * NOT used by the jízda-editor autocomplete: that field keeps the single-line
 * `formatPlaceCopy` (contract §E).
 */
export const formatPlaceSms = (
  place: Pick<PlaceDto, 'name' | 'address' | 'contact' | 'note'>,
): string => {
  const contact = place.contact?.trim() ?? '';
  return [
    place.name.trim(),
    place.address?.trim() ?? '',
    contact.length > 0 ? `${SMS_CONTACT_PREFIX}${contact}` : '',
    place.note?.trim() ?? '',
  ]
    .filter((line) => line.length > 0)
    .join('\n');
};

/**
 * Appends a selected place's note to „Poznámka pro dispečera" (contract §E).
 *
 * - place without a note → the field is left exactly as it was;
 * - empty field → just `Název: poznámka`;
 * - field already containing that exact prefix+note → unchanged (re-select and
 *   dialog reopen must not duplicate the line);
 * - otherwise → appended on a NEW line.
 *
 * The result is ordinary field content: the user can edit it before saving.
 */
export const appendPlaceNote = (current: string, place: PlaceSummary): string => {
  const note = place.note?.trim() ?? '';
  if (note.length === 0) return current;
  const prefix = `${place.name}: ${note}`;
  if (current.trim().length === 0) return prefix;
  if (current.includes(prefix)) return current;
  return `${current}\n${prefix}`;
};
